import axiosInstance from "@/lib/axiosInstance";
import { DynamicForm } from "@/components/domain/WorkOrder/Mobile/DynamicForm/DynamicForm";
import { DynamicFormFooter } from "@/components/domain/WorkOrder/Mobile/DynamicForm/DynamicFormFooter/DynamicFooter";
import type { FormPage } from "@/components/features/FormBuilder/types/form.types";
import type { FormLogicContext } from "@/components/features/FormBuilder/data/contextTriggers";
import { authGuardWithPermissions } from "@/utils/authGuard";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, retainSearchParams } from "@tanstack/react-router";
import {
  leaveRecordTenant,
  recordTenantSearchSchema,
  syncRecordTenant,
} from "@/utils/recordTenantRoute";
import { useState } from "react";

// Temporary verification route for SERV-2505 — not wired into production visit detail yet.

export interface ResolvedForm {
  assignmentId: string;
  visitId: string;
  formType: string;
  sourceLevel: { org: string; work: string };
  templateId: string;
  templateName: string;
  templateVersionId: string;
  version: number;
  schema: unknown;
  resolveToken?: string;
  context?: FormLogicContext;
}

export interface ResolvedFormSlot {
  formType:
    | "check_in"
    | "check_out"
    | "assessment"
    | "further_assessment"
    | "different_trade_assessment"
    | "another_opinion"
    | "repair"
    | "different_trade_repair"
    | "assumed_recall"
    | "recall"
    | "pm_visit"
    | "survey_visit"
    | "walkthrough_visit"
    | "recurring_visit"
    | "dispatch_call";
  form: ResolvedForm | null;
}

const MANDATORY_ORDER = ["check_in", "check_out"] as const;

export function groupSlots(slots: ResolvedFormSlot[]): {
  mandatory: ResolvedFormSlot[];
  other: ResolvedFormSlot[];
} {
  const mandatory = MANDATORY_ORDER.map((formType) =>
    slots.find((slot) => slot.formType === formType),
  ).filter((slot): slot is ResolvedFormSlot => slot !== undefined);

  const mandatoryTypes = new Set(mandatory.map((slot) => slot.formType));
  const other = slots.filter((slot) => !mandatoryTypes.has(slot.formType));

  return { mandatory, other };
}

async function fetchResolvedForms(
  workOrderId: string,
  visitId: string,
): Promise<ResolvedFormSlot[]> {
  const response = await axiosInstance.get<{ data: ResolvedFormSlot[] }>(
    `/form-builder/work-orders/${workOrderId}/visits/${visitId}/forms`,
  );
  return response.data.data;
}

interface SaveFormResponsePayload {
  formType: string;
  templateId: string;
  formVersionId: string;
  resolveToken: string;
  answers: Record<string, unknown>;
}

async function saveFormResponse(
  workOrderId: string,
  visitId: string,
  payload: SaveFormResponsePayload,
) {
  const response = await axiosInstance.post(
    `/form-builder/work-orders/${workOrderId}/visits/${visitId}/form-responses`,
    payload,
  );
  return response.data.data;
}

export interface SavedFormResponse {
  responseId: string;
  visitId: string;
  formType: string;
  templateId: string;
  templateName: string;
  versionId: string;
  versionNo: number;
  templateVersionId: string;
  definitionSnapshot: unknown;
  answers: Record<string, unknown>;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
}

async function fetchSavedResponses(
  workOrderId: string,
  visitId: string,
): Promise<SavedFormResponse[]> {
  const response = await axiosInstance.get<{ data: SavedFormResponse[] }>(
    `/form-builder/work-orders/${workOrderId}/form-responses`,
    { params: { visitId } },
  );
  return response.data.data;
}

function SlotCard({
  slot,
  onFill,
}: {
  slot: ResolvedFormSlot;
  onFill: (slot: ResolvedFormSlot) => void;
}) {
  return (
    <Card withBorder padding="sm" radius="md" data-testid={`slot-card-${slot.formType}`}>
      <Group justify="space-between" mb="xs">
        <Badge color="green">{slot.formType}</Badge>
        <Group gap="xs">
          <Badge color={slot.form ? "blue" : "gray"}>
            {slot.form ? "Resolved" : "Not Assigned"}
          </Badge>
          {slot.form && (
            <Button
              size="xs"
              variant="light"
              data-testid={`fill-form-${slot.formType}`}
              onClick={() => onFill(slot)}
            >
              Fill Form
            </Button>
          )}
        </Group>
      </Group>
      <Text fw={500}>{slot.form?.templateName ?? "—"}</Text>
    </Card>
  );
}

function FillFormPanel({
  workOrderId,
  visitId,
  slot,
  onDone,
}: {
  workOrderId: string;
  visitId: string;
  slot: ResolvedFormSlot;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const form = slot.form;

  const mutation = useMutation({
    mutationFn: (answers: Record<string, unknown>) =>
      saveFormResponse(workOrderId, visitId, {
        formType: slot.formType,
        templateId: form?.templateId ?? "",
        formVersionId: form?.templateVersionId ?? "",
        resolveToken: form?.resolveToken ?? "",
        answers,
      }),
    onSuccess: () => {
      notifications.show({
        color: "green",
        title: "Form saved",
        message: `${form?.templateName ?? slot.formType} response saved.`,
      });
      void queryClient.invalidateQueries({ queryKey: ["temp-visit-resolved-forms"] });
      void queryClient.invalidateQueries({ queryKey: ["temp-visit-saved-responses"] });
      onDone();
    },
    onError: (error) => {
      notifications.show({
        color: "red",
        title: "Failed to save form",
        message: error instanceof Error ? error.message : "Unknown error",
      });
    },
  });

  if (!form) return null;

  // form.schema comes straight from the backend's stored template definition —
  // not every template has been authored with a FormPage[] structure (some are
  // still `{}` or `{ sections: [] }`), so this must not assume it's an array.
  const pages = Array.isArray(form.schema) ? (form.schema as FormPage[]) : [];

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="md">
        <Title order={4}>{form.templateName}</Title>
        <Button size="xs" variant="subtle" onClick={onDone}>
          Cancel
        </Button>
      </Group>
      {pages.length === 0 ? (
        <Text c="dimmed">This template has no fillable schema.</Text>
      ) : (
        <DynamicForm
          pages={pages}
          context={form.context}
          onSubmit={(data) => mutation.mutate(data)}
        >
          <DynamicFormFooter />
        </DynamicForm>
      )}
    </Card>
  );
}

function SavedResponsesSection({ workOrderId, visitId }: { workOrderId: string; visitId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["temp-visit-saved-responses", workOrderId, visitId],
    queryFn: () => fetchSavedResponses(workOrderId, visitId),
  });

  return (
    <Stack gap="sm" data-testid="saved-responses-section">
      <Title order={4}>Saved Responses</Title>
      {isLoading && <Loader size="sm" />}
      {isError && (
        <Alert color="red" title="Failed to load saved responses">
          Could not fetch saved form responses.
        </Alert>
      )}
      {!isLoading && !isError && (data ?? []).length === 0 && (
        <Text c="dimmed">No responses saved yet.</Text>
      )}
      {(data ?? []).map((response) => (
        <Card
          key={response.responseId}
          withBorder
          padding="sm"
          radius="md"
          data-testid={`saved-response-${response.formType}`}
        >
          <Group justify="space-between" mb="xs">
            <Badge color="green">{response.formType}</Badge>
            <Badge color="gray">v{response.versionNo}</Badge>
          </Group>
          <Text fw={500}>{response.templateName}</Text>
          <Text size="sm" c="dimmed">
            {response.createdByName ?? response.createdBy} ·{" "}
            {new Date(response.createdAt).toLocaleString()}
          </Text>
          <Text size="sm" fw={500} mt="sm">
            Answers
          </Text>
          <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
            {JSON.stringify(response.answers, null, 2)}
          </pre>
        </Card>
      ))}
    </Stack>
  );
}

function ResolvedFormsPage() {
  const { workOrderId, visitId } = Route.useParams();
  const [fillingFormType, setFillingFormType] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["temp-visit-resolved-forms", workOrderId, visitId],
    queryFn: () => fetchResolvedForms(workOrderId, visitId),
  });

  const fillingSlot = (data ?? []).find((slot) => slot.formType === fillingFormType) ?? null;

  if (fillingSlot) {
    return (
      <Container py="xl">
        <FillFormPanel
          workOrderId={workOrderId}
          visitId={visitId}
          slot={fillingSlot}
          onDone={() => setFillingFormType(null)}
        />
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container py="xl">
        <Loader />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container py="xl">
        <Alert color="red" title="Failed to load resolved forms">
          {error instanceof Error ? error.message : "Unknown error"}
        </Alert>
      </Container>
    );
  }

  const { mandatory, other } = groupSlots(data ?? []);

  return (
    <Container py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>Resolved Forms</Title>
          <Text c="dimmed">
            Work Order {workOrderId} · Visit {visitId}
          </Text>
        </div>

        <Stack gap="sm">
          <Title order={4}>Mandatory</Title>
          {mandatory.length === 0 ? (
            <Text c="dimmed">No mandatory slots returned.</Text>
          ) : (
            mandatory.map((slot) => (
              <SlotCard
                key={slot.formType}
                slot={slot}
                onFill={(s) => setFillingFormType(s.formType)}
              />
            ))
          )}
        </Stack>

        <Stack gap="sm">
          <Title order={4}>Other</Title>
          {other.length === 0 ? (
            <Text c="dimmed">No other slots returned.</Text>
          ) : (
            other.map((slot) => (
              <SlotCard
                key={slot.formType}
                slot={slot}
                onFill={(s) => setFillingFormType(s.formType)}
              />
            ))
          )}
        </Stack>

        <SavedResponsesSection workOrderId={workOrderId} visitId={visitId} />
      </Stack>
    </Container>
  );
}

export const Route = createFileRoute(
  "/_authenticated/work-orders/$workOrderId/$visitId/resolved-forms",
)({
  component: ResolvedFormsPage,
  validateSearch: recordTenantSearchSchema.parse,
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});
